import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';

import { isValid, parse, isEighteenOrOlder } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import { spokenFieldHint } from '@eneco/components/spoken-field/spoken-field';
import { useFormatter } from '@eneco/i18n';
import { Grid, InputDate, InputText, RadioButton, RadioGroup, VisuallyHidden } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { PersonalDetailsStepRendering } from '@sitecore/types/PersonalDetailsStep';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import { StepShell } from '../../components/StepShell/StepShell';
import { formatInitials, addLeadingZero } from '../../helpers';
import { useFlowStep } from '../../hooks';
import { NAME_PATTERN, OPTIONAL_NAME_PATTERN } from '../validationPatterns';

/** Group + option labels come from Sitecore; this is the SR confirmation sentence. */
const SALUTATION_SELECTION_MESSAGE = '{groupLabel}: {label} gekozen.';

interface FormFields {
  salutation: string;
  initials: string;
  firstName: string;
  surnamePreposition: string;
  surname: string;
  birthdate: string;
  day: string | number;
  month: string | number;
  year: string | number;
}

export const PersonalDetailsStep: FC = () => {
  const { PersonalDetailsStep: { fields: textualData } = {} } = usePlaceholderContent<{
    PersonalDetailsStep: PersonalDetailsStepRendering;
  }>();

  const { format, date } = useFormatter();
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');
  const salutationRadio = textualData?.personalDetails?.salutationRadio?.value;

  const buildSchema = () =>
    yup.object({
      salutation: yup.string().required(textualData?.personalDetails?.salutationRadio?.value?.requiredMessage),
      initials: yup
        .string()
        .required(textualData?.personalDetails?.initialsFormField?.value?.requiredMessage)
        .test(
          'initials max',
          textualData?.personalDetails?.initialsFormField?.value?.validationMaxValueMessage ?? '',
          (value = '') => {
            return value.toUpperCase().replace(/[^A-Z]/g, '').length <= 6;
          },
        ),
      firstName: yup
        .string()
        .required(textualData?.personalDetails?.firstNameFormField?.value.requiredMessage)
        .matches(NAME_PATTERN, textualData?.personalDetails?.firstNameFormField?.value.validationMessage),
      surnamePreposition: yup
        .string()
        .matches(
          OPTIONAL_NAME_PATTERN,
          textualData?.personalDetails?.surnamePrepositionFormField.value.validationMessage,
        ),
      surname: yup
        .string()
        .required(textualData?.personalDetails?.surnameFormField?.value.requiredMessage)
        .matches(NAME_PATTERN, textualData?.personalDetails?.surnameFormField?.value.validationMessage),
      day: yup
        .number()
        .typeError(textualData?.personalDetails?.dayFormField?.value?.validationMessage ?? '')
        .required(textualData?.personalDetails?.dayFormField?.value?.requiredMessage)
        .min(1, textualData?.personalDetails?.dayFormField?.value?.validationMessage)
        .max(31, textualData?.personalDetails?.dayFormField?.value?.validationMessage),
      month: yup
        .number()
        .typeError(textualData?.personalDetails?.monthFormField?.value?.validationMessage ?? '')
        .required(textualData?.personalDetails?.monthFormField?.value?.requiredMessage)
        .min(1, textualData?.personalDetails?.monthFormField?.value?.validationMessage)
        .max(12, textualData?.personalDetails?.monthFormField?.value?.validationMessage),
      year: yup
        .number()
        .typeError(textualData?.personalDetails?.yearFormField?.value?.validationMessage ?? '')
        .required(textualData?.personalDetails?.yearFormField?.value?.requiredMessage)
        .min(1900, textualData?.personalDetails?.yearFormField?.value?.validationMessage),
      birthdate: yup
        .object({})
        .test(
          'valid date',
          textualData?.personalDetails?.birthdateFormField?.value?.validationMessage ?? '',
          (_, context) => {
            const {
              parent: { day, month, year },
            } = context;

            if (!day || !month || !year) {
              return false;
            }

            return isValid(year, month, day);
          },
        )
        .test(
          'minimum age',
          textualData?.personalDetails?.birthdateFormField?.value?.validationMinValueMessage ?? '',
          (_, context) => {
            const {
              parent: { day, month, year },
            } = context;
            return isEighteenOrOlder(year, month, day);
          },
        ),
    });

  const {
    form: {
      handleSubmit,
      control,
      setValue,
      register,
      formState: { errors, isSubmitSuccessful, submitCount },
    },
  } = useFlowStep<FormFields>({
    buildSchema,
    mode: 'onTouched',
    defaultValues: context => ({
      salutation: context.salutation,
      initials: context.initials,
      firstName: context.firstName,
      surnamePreposition: context.surnamePreposition,
      surname: context.surname,
      day: context.birthdate && addLeadingZero(parse(context.birthdate).getDate()),
      month: context.birthdate && addLeadingZero(parse(context.birthdate).getMonth() + 1),
      year: context.birthdate && parse(context.birthdate).getFullYear(),
    }),
    onSubmitSuccess: formData => ({
      salutation: formData.salutation,
      initials: formData.initials,
      firstName: formData.firstName,
      surnamePreposition: formData.surnamePreposition || undefined,
      surname: formData.surname,
      birthdate: date.short(new Date(Number(formData.year), Number(formData.month) - 1, Number(formData.day))),
    }),
  });

  const salutationGroupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!submitCount || !errors.salutation?.message) return;

    // react-hook-form focuses the next native field on a timeout; wait so aanhef wins.
    const timeoutId = window.setTimeout(() => {
      salutationGroupRef.current?.querySelector<HTMLElement>('[role="radio"]')?.focus();
    });

    return () => window.clearTimeout(timeoutId);
  }, [submitCount, errors.salutation?.message]);

  return (
    <StepShell
      handleSubmit={handleSubmit}
      textualData={textualData}
      isLoading={isSubmitSuccessful}
      nextButtonLabel={textualData?.content?.nextStepText?.value}>
      <VisuallyHidden ariaLive="polite">{selectionAnnouncement}</VisuallyHidden>
      <Controller
        control={control}
        name="salutation"
        render={({ field: { onChange, value, name, ref } }) => (
          <RadioGroup
            ref={node => {
              salutationGroupRef.current = node;
              ref(node);
            }}
            label={salutationRadio?.label ?? ''}
            name={name}
            direction="row"
            value={value}
            onValueChange={next => {
              onChange(next);
              const optionLabel = salutationRadio?.options?.find(option => option.name === next)?.label;
              if (!salutationRadio?.label || !optionLabel) return;
              setSelectionAnnouncement(
                format(SALUTATION_SELECTION_MESSAGE, { groupLabel: salutationRadio.label, label: optionLabel }),
              );
            }}
            error={errors.salutation?.message}>
            {salutationRadio?.options?.map(option => (
              <RadioButton value={option.name} key={option.name}>
                {option.label}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />

      <InputText
        {...register('firstName', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('firstName', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.firstNameFormField?.value?.label ?? ''}
        hint={spokenFieldHint(
          textualData?.personalDetails?.firstNameFormField?.value?.hint,
          textualData?.personalDetails?.firstNameFormField?.value?.placeholder,
        )}
        autoComplete="given-name"
        error={errors.firstName?.message}
      />

      <Grid columns="2" columnGap="3">
        <InputText
          {...register('initials', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              setValue('initials', formatInitials(event.target.value));
            },
          })}
          label={textualData?.personalDetails?.initialsFormField?.value?.label ?? ''}
          hint={spokenFieldHint(
            textualData?.personalDetails?.initialsFormField?.value?.hint,
            textualData?.personalDetails?.initialsFormField?.value?.placeholder,
          )}
          error={errors.initials?.message}
        />
        <InputText
          {...register('surnamePreposition')}
          label={textualData?.personalDetails?.surnamePrepositionFormField?.value?.label ?? ''}
          hint={spokenFieldHint(
            textualData?.personalDetails?.surnamePrepositionFormField?.value?.hint,
            textualData?.personalDetails?.surnamePrepositionFormField?.value?.placeholder,
          )}
          error={errors.surnamePreposition?.message}
          isOptional
        />
      </Grid>

      <InputText
        {...register('surname', {
          onBlur: (event: ChangeEvent<HTMLInputElement>) => {
            setValue('surname', capitalizeFirstLetter(event?.target?.value?.trim()));
          },
        })}
        label={textualData?.personalDetails?.surnameFormField?.value?.label ?? ''}
        hint={spokenFieldHint(
          textualData?.personalDetails?.surnameFormField?.value?.hint,
          textualData?.personalDetails?.surnameFormField?.value?.placeholder,
        )}
        autoComplete="family-name"
        error={errors.surname?.message}
      />

      <InputDate
        label={textualData?.personalDetails?.birthdateFormField?.value?.label ?? ''}
        hint={textualData?.personalDetails?.birthdateFormField?.value?.hint}
        name="birthdate"
        autoComplete="bday"
        error={errors.birthdate?.message ?? errors.year?.message ?? errors.month?.message ?? errors.day?.message}>
        <InputDate.Day
          {...register('day', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value) setValue('day', addLeadingZero(event.target.value));
            },
          })}
          label={textualData?.personalDetails?.dayFormField?.value?.label ?? ''}
          hasError={!!(errors.day?.message || errors.birthdate?.message)}
        />
        <InputDate.Month
          {...register('month', {
            onBlur: (event: ChangeEvent<HTMLInputElement>) => {
              if (event.target.value) setValue('month', addLeadingZero(event.target.value));
            },
          })}
          label={textualData?.personalDetails?.monthFormField?.value?.label ?? ''}
          hasError={!!(errors.month?.message || errors.birthdate?.message)}
        />
        <InputDate.Year
          {...register('year')}
          label={textualData?.personalDetails?.yearFormField?.value?.label ?? ''}
          hasError={!!(errors.year?.message || errors.birthdate?.message)}
        />
      </InputDate>
    </StepShell>
  );
};
